import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../../api/api";
import PropTypes from "prop-types";
import { connect } from "react-redux";

//Utils

import { getTimeOfDayLocalized } from "../../../utils/date";
import { preventWidows } from "../../../utils/text";
import { shortInteractionCount } from "../../../utils/post";

//Components
import { Typography, IconButton, Tooltip } from "@mui/material";
import ImageCarousel from "../../feed/types/components/ImageCarousel";
import { Link } from "react-router-dom";
import RecipeCard from "../../recipes/card/RecipeCard";
import HistoryItemMacroSection from "./HistoryItemMacroSection";
import HistoryItemPantrySection from "./HistoryItemPantrySection";

//Modals
import CustomModal from "../../modal/CustomModal";
import PostCommentModal from "../../feed/modals/PostCommentModal";
import PostShareModal from "../../feed/modals/PostShareModal";
import DeleteHistoryItemConfirm from "../modals/DeleteHistoryItemConfirm";

//Icons
import { ReactComponent as ClockIcon } from "../../../assets/icons/svg/clock.svg";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ReactComponent as FilledHeartIcon } from "../../../assets/icons/svg/heart-solid.svg";
import { ReactComponent as HeartIcon } from "../../../assets/icons/svg/heart.svg";
import { ReactComponent as CommentIcon } from "../../../assets/icons/svg/comment.svg";
import { ReactComponent as ShareIcon } from "../../../assets/icons/svg/new-message.svg";
import { ReactComponent as BookmarkIcon } from "../../../assets/icons/svg/bookmark.svg";
import { ReactComponent as FilledBookmarkIcon } from "../../../assets/icons/svg/bookmark_filled.svg";
import { ReactComponent as RepostIcon } from "../../../assets/icons/svg/repost.svg";
import { ReactComponent as StarIcon } from "../../../assets/icons/svg/star-solid.svg";
import { ReactComponent as ChefIcon } from "../../../assets/icons/svg/hat-chef-light.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/svg/x-mark.svg";

//Actions
import { setAlert } from "../../../actions/alert";

const CollapsibleSection = ({ title, children, expanded, onToggle, count, rounded = "", border = false }) => {
  return (
    <div
      className={`flex flex-col gap-0 border-t ${
        border ? "border-separator" : "border-transparent"
      } bg-background ${rounded}`}
    >
      <div className="flex items-center cursor-pointer p-4" onClick={onToggle}>
        <div className="flex-1 flex items-center gap-2">
          <Typography variant="subtitle2" className="font-normal leading-snug text-primaryText">
            {title} {count && <span className="text-mainGreen font-medium ml-1 text-sm leading-snug">{count}</span>}
          </Typography>
          <motion.div initial={false} animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronDown className="w-5 h-5 text-secondaryText-600" />
          </motion.div>
        </div>
      </div>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="bg-background md:rounded-b-md">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const RecipeEvent = ({ data, historyIndex, setAlert }) => {
  const [showMacros, setShowMacros] = useState(false);
  const [showPantry, setShowPantry] = useState(false);
  const [totalCalories, setTotalCalories] = useState(null);

  //Interaction State
  const [hasLiked, setHasLiked] = useState(data?.recipe?.interaction?.post?.hasLiked || false);
  const [likesCount, setLikesCount] = useState(data?.recipe?.interaction?.post?.likesCount || 0);
  const [commentsCount, setCommentsCount] = useState(data?.recipe?.interaction?.post?.commentsCount || 0);
  const [repostsCount, setRepostsCount] = useState(data?.recipe?.interaction?.post?.repostsCount || 0);
  const [repostLoading, setRepostLoading] = useState(false);
  const [hasCommented, setHasCommented] = useState(data?.recipe?.interaction?.post?.hasCommented || false);
  const [hasPinned, setHasPinned] = useState(data?.recipe?.interaction?.post?.hasPinned || false);
  const [hasReposted, setHasReposted] = useState(data?.recipe?.interaction?.post?.hasReposted || false);
  const [hasBookmarked, setHasBookmarked] = useState(data?.recipe?.interaction?.hasBookmarked || false);
  const [bookmarksCount, setBookmarksCount] = useState(data?.recipe?.interaction?.bookmarksCount || 0);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const onLike = async () => {
    if (data?.recipe?.interaction?.post?._id) {
      try {
        setHasLiked(true);
        setLikesCount(likesCount + 1);
        const res = await api.post(`/post/like/${data?.recipe?.interaction?.post?._id}`);
      } catch (error) {
        console.log(error);
        setHasLiked(false);
        setLikesCount(likesCount - 1 > 0 ? likesCount - 1 : 0);
      }
    }
  };

  const onUnlike = async () => {
    if (data?.recipe?.interaction?.post?._id) {
      setHasLiked(false);
      setLikesCount(likesCount - 1 > 0 ? likesCount - 1 : 0);
      try {
        const res = await api.post(`/post/unlike/${data?.recipe?.interaction?.post?._id}`);
      } catch (error) {
        console.log(error);
        setHasLiked(true);
        setLikesCount(likesCount + 1);
      }
    }
  };

  const handleCommentsClick = () => {
    setShowCommentModal(true);
  };

  const handleCommentsClose = () => {
    setShowCommentModal(false);
  };

  const handleRepost = async () => {
    setShowShareModal(false);
    setRepostLoading(true);
    if (hasReposted) {
      try {
        setHasReposted(false);
        setRepostsCount(repostsCount - 1 > 0 ? repostsCount - 1 : 0);
        const res = await api.delete(`/post/unrepost/${data?.recipe?.interaction?.post?._id}`);
        setAlert("Post removed from your profile", "success", 3000);
        setRepostLoading(false);
      } catch (error) {
        console.log(error);
        setAlert("Error removing post from your profile. Please refresh and try again", "error", 3000);
        setHasReposted(true);
        if (repostsCount !== 0) {
          setRepostsCount(repostsCount + 1);
        }
        setRepostLoading(false);
      }
    } else {
      try {
        setHasReposted(true);
        setRepostsCount(repostsCount + 1);
        const res = await api.post(`/post/repost/${data?.recipe?.interaction?.post?._id}`);
        setAlert("Post reposted to your profile", "success", 3000);
        setRepostLoading(false);
      } catch (error) {
        console.log(error);
        setAlert("Error reposting post. Please refresh and try again", "error", 3000);
        setRepostLoading(false);
        setHasReposted(false);
        setRepostsCount(repostsCount - 1 > 0 ? repostsCount - 1 : 0);
      }
    }
  };

  const onBookmark = async () => {
    try {
      if (!bookmarkLoading) {
        setBookmarkLoading(true);
        setHasBookmarked(true);
        setBookmarksCount(bookmarksCount + 1);
        const res = await api.post(`/recipes/bookmark/${data?.recipe?.id}`);

        setAlert(`${data?.recipe?.name} added to bookmarks`, "success", 3000);
        setBookmarkLoading(false);
      }
    } catch (error) {
      console.log(error);
      setBookmarksCount(bookmarksCount - 1 > 0 ? bookmarksCount - 1 : 0);
      setBookmarkLoading(false);
      setHasBookmarked(false);
    }
  };

  const onUnbookmark = async () => {
    try {
      if (!bookmarkLoading) {
        setBookmarkLoading(true);
        setHasBookmarked(false);
        setBookmarksCount(bookmarksCount - 1 > 0 ? bookmarksCount - 1 : 0);
        const res = await api.post(`/recipes/unbookmark/${data?.recipe?.id}`);

        setBookmarkLoading(false);
      }
    } catch (error) {
      console.log(error);
      if (bookmarksCount !== 0) {
        setBookmarksCount(bookmarksCount + 1);
      }

      setBookmarkLoading(false);
      setHasBookmarked(true);
    }
  };

  const calcTotalCalories = () => {
    if (!data?.macros?.sections || data?.macros?.sections.length === 0) {
      return null;
    }

    let totalCalories = 0;
    for (let i = 0; i < data?.macros?.sections.length; i++) {
      const section = data?.macros?.sections[i];
      let servingsEaten = data?.macros?.sections[i].servingsEaten || 1;

      for (let j = 0; j < section.macros.length; j++) {
        if (section.macros[j]?.name === "Calories") {
          totalCalories += section.macros[j]?.amount * servingsEaten;
        }
      }
    }

    if (totalCalories === 0) {
      return null;
    }
    return totalCalories;
  };

  const handleDelete = async () => {
    setShowDeleteModal(false);
  };

  useEffect(() => {
    const total = calcTotalCalories();
    setTotalCalories(total);
  }, [data]);

  return (
    <div className="w-full border-y md:border border-separator rounded-md shadow-sm bg-background flex flex-col">
      {data?.recipe?.interaction?.post?._id && (
        <>
          <CustomModal open={showCommentModal} handleClose={handleCommentsClose} type="bottom">
            <PostCommentModal
              postid={data?.recipe?.interaction?.post?._id}
              handleClose={handleCommentsClose}
              commentCount={commentsCount}
              setCommentCount={setCommentsCount}
            />
          </CustomModal>
          <CustomModal
            open={showShareModal}
            handleClose={() => setShowShareModal(false)}
            contentStyleOverride={{ marginBottom: 0 }}
          >
            <PostShareModal
              post={{
                id: data?.recipe?.interaction?.post?._id || null,
                interactions: {
                  hasPinned: hasPinned,
                },
                user: {
                  username: data?.recipe?.user?.username || "unknown",
                  _id: data?.recipe?.user?.id || null,
                },
                recipe: {
                  _id: data?.recipe?._id || null,
                  url: data?.recipe?.url || "",
                },
              }}
              handleClose={() => setShowShareModal(false)}
              blockRepost={data?.recipe?.interaction?.post?.blockRepost || true}
              handleRepost={handleRepost}
              hasReposted={hasReposted}
            />
          </CustomModal>
        </>
      )}
      <CustomModal open={showDeleteModal} handleClose={() => setShowDeleteModal(false)} type="bottomToCentered">
        <DeleteHistoryItemConfirm handleDelete={handleDelete} name={data.recipe.name} macroID={data.macros.id} />
      </CustomModal>
      <div className="bg-neutral-50 border-b border-separator flex flex-col md:flex-row md:rounded-tl-md">
        <div className="w-full md:w-[300px] relative">
          <div className="w-full pb-[100%] md:pb-0 md:h-[300px]">
            <div className="absolute inset-0">
              <ImageCarousel data={data.recipe.data} showEmpty={true} rootStyles="md:rounded-tl-md" />
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div>
            <div className="bg-stone-800 md:rounded-tr-md flex flex-row justify-between items-start">
              <Link to={`/${data?.recipe?.user?.username}/${data.recipe.url}`}>
                <div className="p-4">
                  <Typography variant="h6" className="font-normal leading-snug text-background">
                    {preventWidows(data.recipe.name)}
                  </Typography>

                  <Typography variant="subtitle2" className="text-mainGreen-300 leading-snug font-normal">
                    @{data.recipe.user.username}
                  </Typography>
                  {data?.recipe?.interaction && (
                    <div className="flex flex-row gap-4 mt-3">
                      <>
                        <Tooltip
                          title={
                            data.recipe.interaction.ratingAverage !== -1
                              ? `This recipe has been rated ${
                                  data.recipe.interaction.ratingAverage
                                } stars with ${shortInteractionCount(data.recipe.interaction.ratingCount)} review${
                                  data.recipe.interaction.ratingCount !== 1 ? "s" : ""
                                }`
                              : "This recipe has not been rated yet"
                          }
                          placement="top-start"
                          arrow
                        >
                          <div className="flex flex-row items-center cursor-pointer select-none">
                            <div className="w-full">
                              <StarIcon className="h-3 fill-rating mr-1" />
                            </div>
                            <div className="w-full flex justify-center">
                              <Typography
                                variant="body2"
                                className="font-normal break-keep whitespace-nowrap select-none text-secondaryText-100 leading-none text-xs"
                              >
                                {data.recipe.interaction.ratingAverage !== -1
                                  ? data.recipe.interaction.ratingAverage
                                  : "-"}
                              </Typography>
                            </div>
                          </div>
                        </Tooltip>
                        <Tooltip
                          title={`This recipe has been made ${shortInteractionCount(
                            data.recipe.interaction.cookedCount
                          )} time${data.recipe.interaction.cookedCount !== 1 && "s"}`}
                          placement="top-start"
                          arrow
                        >
                          <div className="flex flex-row items-center justify-between cursor-pointer select-none">
                            <div className="w-full">
                              <ChefIcon className="h-3 fill-secondaryText-50 mr-1" />
                            </div>

                            <div className="w-full flex justify-center">
                              <Typography
                                variant="body2"
                                className="font-normal select-none text-secondaryText-100 leading-none text-xs"
                              >
                                {data.recipe.interaction.cookedCount > 0
                                  ? shortInteractionCount(data.recipe.interaction.cookedCount)
                                  : "-"}
                              </Typography>
                            </div>
                          </div>
                        </Tooltip>
                      </>
                    </div>
                  )}
                </div>
              </Link>
              <IconButton className="pr-4 pt-4" onClick={() => setShowDeleteModal(true)}>
                <DeleteIcon className="h-4 fill-secondaryText-50" />
              </IconButton>
            </div>
          </div>
          <div className="flex flex-col h-full justify-between">
            <div className="flex flex-row justify-between items-start p-4">
              <div className="flex flex-col">
                <Typography
                  variant="body1"
                  className="body1 text-secondaryText-600 flex flex-row gap-2 items-center text-xs"
                >
                  <ClockIcon className="w-3 h-3 fill-secondaryText-600" />
                  {getTimeOfDayLocalized(new Date(data.date))}
                </Typography>
              </div>

              {totalCalories !== null && (
                <Typography
                  variant="h5"
                  className=" text-mainGreen-500 flex flex-row items-start leading-none font-normal"
                >
                  +{totalCalories}
                  <span className="text-xs leading-none text-secondaryText-600 font-normal ml-1">kcal</span>
                </Typography>
              )}
            </div>
            <div className="flex flex-col p-4 gap-2">
              {data?.recipe?.interaction && (
                <div className="w-full flex flex-row justify-between">
                  <div className="flex flex-row justify-between w-full">
                    <div className="flex flex-row">
                      <div className="flex flex-col items-center mr-4">
                        <div className="w-full relative h-[1.75rem]">
                          {hasLiked ? (
                            <IconButton
                              className="p-0 hover:bg-transparent"
                              disableRipple
                              onClick={() => onUnlike()}
                              size="large"
                            >
                              <FilledHeartIcon className="h-[1.5rem] fill-mainGreen hover:fill-mainGreen-600" />
                            </IconButton>
                          ) : (
                            <IconButton
                              className="p-0 hover:bg-transparent"
                              disableRipple
                              onClick={() => onLike()}
                              size="large"
                            >
                              <HeartIcon className="h-[1.5rem] hover:fill-secondaryText" />
                            </IconButton>
                          )}
                        </div>

                        <Typography variant="body2" className="text-secondaryText">
                          {shortInteractionCount(likesCount)}
                        </Typography>
                      </div>
                      <div className="flex flex-col items-center mr-4">
                        <div className="w-full relative h-[1.75rem]">
                          <IconButton
                            className="p-0 hover:bg-transparent"
                            disableRipple
                            onClick={() => handleCommentsClick()}
                            size="large"
                          >
                            <CommentIcon
                              className={`h-[1.5rem] t ${
                                hasCommented
                                  ? "fill-mainGreen hover:fill-mainGreen-600"
                                  : "fill-primaryText hover:fill-secondaryText"
                              }`}
                            />
                          </IconButton>
                        </div>

                        <Typography variant="body2" className="text-secondaryText">
                          {shortInteractionCount(commentsCount)}
                        </Typography>
                      </div>
                      <div className="flex flex-col items-center mr-4">
                        <div className="w-full relative h-[1.75rem]">
                          <IconButton
                            className="p-0 hover:bg-transparent"
                            disableRipple
                            onClick={() => setShowShareModal(true)}
                            size="large"
                            disabled={repostLoading}
                          >
                            <ShareIcon className="h-[1.5rem] hover:fill-secondaryText" />
                          </IconButton>
                        </div>

                        <Typography variant="body2" className="text-secondaryText">
                          {shortInteractionCount(repostsCount)}
                        </Typography>
                      </div>
                    </div>
                    <div className="flex flex-col items-center ml-4">
                      <div className="w-full relative h-[1.75rem]">
                        {hasBookmarked ? (
                          <IconButton
                            className="p-0 hover:bg-transparent"
                            disableRipple
                            onClick={() => onUnbookmark()}
                            size="large"
                          >
                            <FilledBookmarkIcon className="h-[1.5rem] fill-mainGreen hover:fill-mainGreen-600" />
                          </IconButton>
                        ) : (
                          <IconButton
                            className="p-0 hover:bg-transparent"
                            disableRipple
                            onClick={() => onBookmark()}
                            size="large"
                          >
                            <BookmarkIcon className="h-[1.5rem] hover:fill-secondaryText" />
                          </IconButton>
                        )}
                      </div>

                      <Typography variant="body2" className="text-secondaryText">
                        {shortInteractionCount(bookmarksCount)}
                      </Typography>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        {data?.macros && (
          <CollapsibleSection title="Macronutrients" expanded={showMacros} onToggle={() => setShowMacros(!showMacros)}>
            {data.macros.sections?.map((section, i) => (
              <HistoryItemMacroSection key={`section-${i}`} historyIndex={historyIndex} macroSectionIndex={i} />
            ))}
          </CollapsibleSection>
        )}

        {data?.pantry && (
          <CollapsibleSection
            title="Ingredients"
            expanded={showPantry}
            onToggle={() => setShowPantry(!showPantry)}
            count={data.pantry.sections?.reduce(
              (acc, section) => acc + (section.ingredientsUsed?.length || 0) + (section.ingredientsAdded?.length || 0),
              0
            )}
            rounded="md:rounded-b-md"
            border={true}
          >
            {data.pantry.sections?.map((section, i) => (
              <HistoryItemPantrySection
                key={`pantry-section-${i}`}
                section={section}
                historyIndex={historyIndex}
                pantrySectionIndex={i}
              />
            ))}
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
};

//TODO: Pantry only events
const MealSectionItem = ({ course, data, historyIndex, setAlert }) => {
  return (
    <div>{data.recipe ? <RecipeEvent data={data} historyIndex={historyIndex} setAlert={setAlert} /> : <div></div>}</div>
  );
};

MealSectionItem.propTypes = {
  course: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  historyIndex: PropTypes.number.isRequired,
  setAlert: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, { setAlert })(MealSectionItem);
